/**
 * Shared Auth Utilities
 * 
 * Provides token management with automatic refresh for API calls.
 * Uses window.Auth (from public/shared/js/auth.js) for all environments.
 */

import { navigateTo } from './navigation';

const TRANSIENT_NETWORK_RE = /failed to fetch|network|timeout|aborted|temporarily unavailable|backend not available|load failed/i;

/** True when a request failed because the network was down, not because the server refused it. */
export function isTransientNetworkError(error) {
  if (!error) return false;
  if (error.name === 'TypeError' || error.name === 'AbortError' || error.name === 'TimeoutError') {
    return true;
  }
  return TRANSIENT_NETWORK_RE.test(String(error.message || error));
}

/**
 * Block until a stored refresh token becomes a live session, or the session is gone.
 * Used after sleep, when the first Cognito call fails with a name-resolution error.
 */
export function waitForRecoverableSession() {
  if (typeof window === 'undefined' || !window.Auth) return Promise.resolve(false);
  if (window.Auth.isAuthenticated()) return Promise.resolve(true);
  if (!localStorage.getItem('refresh_token')) return Promise.resolve(false);

  return new Promise((resolve) => {
    let settled = false;
    const finish = (ok) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('fokus:session-refreshed', onRefresh);
      window.clearInterval(poll);
      resolve(ok);
    };
    const onRefresh = () => {
      if (window.Auth?.isAuthenticated()) finish(true);
    };
    const poll = window.setInterval(() => {
      if (window.Auth?.isAuthenticated()) finish(true);
      else if (!localStorage.getItem('refresh_token')) finish(false);
    }, 500);
    window.addEventListener('fokus:session-refreshed', onRefresh);
    window.Auth.refreshTokenIfNeeded?.();
  });
}

/**
 * Get a fresh JWT token.
 * Restores session from Electron secure store if needed, refreshes when the access
 * token is expired or near expiry (same pattern as Tiedown's getValidToken).
 *
 * @returns {Promise<string>} Fresh ID token for Authorization
 */
export async function getFreshToken() {
  if (!window.Auth) {
    console.error('❌ window.Auth not available');
    throw new Error('Auth system not loaded');
  }

  if (!window.Auth.isAuthenticated()) {
    await window.Auth.ensureSessionReady?.();
  }

  if (window.Auth.isAuthenticated()) {
    await window.Auth.getAccessToken?.();
  }

  if (window.Auth.sessionMatchesThisApp && !window.Auth.sessionMatchesThisApp()) {
    await window.Auth.logout?.();
    throw new Error('This login is for a different Fokus environment');
  }

  if (!window.Auth.isAuthenticated()) {
    // Refresh token is still good — the access token just couldn't be renewed
    // because the network was suspended. Redirecting here reloads the app onto
    // the loading screen and leaves it there.
    if (localStorage.getItem('refresh_token')) {
      throw new Error('Session temporarily unavailable');
    }
    console.log('🔄 Not authenticated, redirecting to login...');
    navigateTo('/auth/login');
    throw new Error('Not authenticated - redirecting to login');
  }

  const token = window.Auth.getIdToken();
  if (!token || typeof token !== 'string') {
    console.error('❌ No token available');
    navigateTo('/auth/login');
    throw new Error('No token available - redirecting to login');
  }

  let normalized = token.trim();
  if (/^bearer\s+/i.test(normalized)) {
    normalized = normalized.replace(/^bearer\s+/i, '').trim();
  }
  if (normalized.split('.').length < 3) {
    console.error('❌ Stored token is not a valid JWT');
    navigateTo('/auth/login');
    throw new Error('Invalid token format');
  }

  return normalized;
}

/**
 * Re-hydrate tokens and refresh after sleep/tab backgrounding (visibility API).
 * Call once from the root auth component; returns a cleanup function.
 */
export function subscribeSessionWakeRefresh() {
  const onVisible = async () => {
    window.Auth?.noteWake?.();
    if (document.hidden || !window.Auth?.ensureSessionReady) return;
    try {
      await window.Auth.ensureSessionReady();
      if (window.Auth.isAuthenticated()) {
        window.Auth.startAutoRefresh?.();
      }
    } catch (e) {
      console.warn('Session refresh on resume failed:', e);
    }
  };
  const onOnline = () => {
    window.Auth?.noteWake?.();
  };
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('online', onOnline);
  window.addEventListener('pageshow', onOnline);
  return () => {
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('online', onOnline);
    window.removeEventListener('pageshow', onOnline);
  };
}

/**
 * Get auth headers for API requests with fresh token
 * 
 * @returns {Promise<Object>} Headers object with Authorization
 */
export async function getAuthHeaders() {
  const token = await getFreshToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
}

export default {
  getFreshToken,
  getAuthHeaders,
  subscribeSessionWakeRefresh,
  isTransientNetworkError,
  waitForRecoverableSession,
};

